// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'token';

/**
 * Secure token storage helper.
 * Uses sessionStorage by default (cleared on tab close — XSS-safer than localStorage),
 * with a one-time migration from any legacy localStorage token.
 */
const tokenStorage = {
  get() {
    let t = sessionStorage.getItem(TOKEN_KEY);
    const legacy = localStorage.getItem(TOKEN_KEY);
    if (!t && legacy) {
      sessionStorage.setItem(TOKEN_KEY, legacy);
      t = legacy;
    }
    // Always strip any leftover localStorage token (one-time migration hygiene).
    if (legacy) localStorage.removeItem(TOKEN_KEY);
    return t;
  },
  set(value) { sessionStorage.setItem(TOKEN_KEY, value); },
  clear() {
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
  },
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => tokenStorage.get());
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    tokenStorage.clear();
    localStorage.removeItem('selectedArea');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;

    tokenStorage.set(access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);

    if (userData.must_change_password) {
      toast.warning('Sua senha foi redefinida por um administrador. Altere-a assim que possível.', { duration: 10000 });
    }

    return userData;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    const { access_token, user: userData } = response.data;

    tokenStorage.set(access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);

    return userData;
  }, []);

  const hasPermission = useCallback((area) => {
    if (!user) return false;
    return user.permissions?.includes(area) || false;
  }, [user]);

  const isAdmin = useCallback(() => {
    return user?.is_admin || false;
  }, [user]);

  const refreshUser = useCallback(async () => {
    if (token) {
      await fetchUser();
    }
  }, [token, fetchUser]);

  const authValue = React.useMemo(() => ({
    user, token, loading, login, register, logout, refreshUser, hasPermission, isAdmin
  }), [user, token, loading, login, register, logout, refreshUser, hasPermission, isAdmin]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
